import { useQuery } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import type { ApiError, TransactionDetail } from '@/api/types'
import { apiRequest } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

type Props = {
  orderId: string | null
  onClose: () => void
  restaurantNameById?: Record<number, string>
}

const centsToCurrency = (cents: number, currency: string) => {
  return (cents / 100).toFixed(2) + ' ' + currency
}

export function InventoryHistoryOrderDrawer({
  orderId,
  onClose,
  restaurantNameById,
}: Props) {
  const { t: tInventory } = useTranslation('inventory')
  const open = !!orderId
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory-order-detail', orderId],
    enabled: open && !!orderId,
    retry: false,
    queryFn: async () =>
      apiRequest<TransactionDetail>(
        `/api/v0/admin/transactions/mobile/${orderId}`,
      ),
  })

  const restaurantName = data
    ? restaurantNameById?.[data.restaurantId]
    : undefined
  const errorMessage = (() => {
    if (!error) return null
    const apiError = error as ApiError
    if (apiError.status === 404) return tInventory('orderDrawer.notFound')
    if (apiError.status === 403) return tInventory('orderDrawer.wrongTenant')
    return tInventory('orderDrawer.loadFailed')
  })()

  return (
    <Drawer
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose()
      }}
    >
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{tInventory('orderDrawer.title')}</DrawerTitle>
          <DrawerDescription>{orderId}</DrawerDescription>
        </DrawerHeader>
        <div className="p-4 space-y-3">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner />
              <span>{tInventory('orderDrawer.loading')}</span>
            </div>
          )}
          {errorMessage && (
            <div className="text-destructive">{errorMessage}</div>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="col-span-2">
                  <span className="text-muted-foreground">
                    {tInventory('orderDrawer.id')}:
                  </span>{' '}
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-mono text-xs underline decoration-dotted"
                    onClick={() => {
                      if (data.id) {
                        navigator.clipboard
                          .writeText(String(data.id))
                          .then(() =>
                            toast.success(
                              tInventory('orderDrawer.copyIdSuccess'),
                            ),
                          )
                          .catch(() =>
                            toast.message(
                              tInventory('orderDrawer.copyFailedTitle'),
                              {
                                description: tInventory(
                                  'orderDrawer.copyFailedDescription',
                                ),
                              },
                            ),
                          )
                      }
                    }}
                    title={tInventory('orderDrawer.copy')}
                  >
                    {data.id}
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tInventory('orderDrawer.time')}:
                  </span>{' '}
                  {new Date(data.occurredAt).toLocaleString()}
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tInventory('orderDrawer.restaurant')}:
                  </span>{' '}
                  {restaurantName ?? data.restaurantId}
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tInventory('orderDrawer.gross')}:
                  </span>{' '}
                  {centsToCurrency(data.totalGrossCents, data.currency)}
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tInventory('orderDrawer.status')}:
                  </span>{' '}
                  {data.status}
                </div>
              </div>
              <div className="mt-3">
                <h3 className="font-medium mb-2">
                  {tInventory('orderDrawer.items')}
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{tInventory('orderDrawer.sku')}</TableHead>
                      <TableHead>{tInventory('orderDrawer.name')}</TableHead>
                      <TableHead>{tInventory('orderDrawer.qty')}</TableHead>
                      <TableHead>{tInventory('orderDrawer.unit')}</TableHead>
                      <TableHead>{tInventory('orderDrawer.line')}</TableHead>
                      <TableHead>{tInventory('orderDrawer.vat')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((it) => (
                      <TableRow key={it.sku + it.name}>
                        <TableCell className="font-mono text-xs">
                          {it.sku}
                        </TableCell>
                        <TableCell>{it.name}</TableCell>
                        <TableCell>{it.qty}</TableCell>
                        <TableCell>
                          {centsToCurrency(it.unitGrossCents, data.currency)}
                        </TableCell>
                        <TableCell>
                          {centsToCurrency(it.lineGrossCents, data.currency)}
                        </TableCell>
                        <TableCell>{it.vatCode || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
        <DrawerFooter>
          <DrawerClose asChild>
            <Button variant="outline">{tInventory('orderDrawer.close')}</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
